import {
  ApiError,
  createVendorSchema,
  formatUsPhone,
  queryKeys,
  VENDOR_EMAIL_TYPES,
  VENDOR_PHONE_TYPES,
  type Vendor,
  type VendorContact,
} from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ExternalLink, Plus, Search, Trash2, Truck } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { InfiniteScrollFooter } from '../components/data-table.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

export function VendorsPage() {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Vendor | null>(null)
  const canWrite = can('inventory:write')

  const filters = { q: search || undefined, pageSize: 50 }
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.vendors.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: Vendor[]; total: number; page: number; pageSize: number }>('/vendors', {
        query: { ...filters, page: pageParam },
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })
  const items = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  const toggleActive = useMutation({
    mutationFn: (input: { id: string; isActive: boolean }) =>
      api.patch(`/vendors/${input.id}`, { isActive: input.isActive }),
    onError: (error) =>
      toast.error('Could not update', error instanceof ApiError ? error.message : undefined),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.vendors.all() }),
  })

  return (
    <Page>
      <PageHeader
        title="Vendors"
        description="Suppliers you buy stock from — contacts with typed phones and emails, address and online order link."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus /> New vendor
            </Button>
          ) : null
        }
      />

      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-foreground-muted" />
        <Input
          className="ps-8"
          placeholder="Search vendors…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-sunken/60">
                <th className="px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted">
                  Name
                </th>
                <th className="px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted">
                  Contact
                </th>
                <th className="px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted">
                  Address
                </th>
                <th className="px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted">
                  Active
                </th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {isLoading ? null : items.length > 0 ? (
                items.map((vendor) => (
                  <tr key={vendor.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 font-medium">
                      <Link
                        to="/vendors/$vendorId"
                        params={{ vendorId: vendor.id }}
                        className="text-primary hover:underline"
                      >
                        {vendor.name}
                      </Link>
                      {vendor.orderUrl ? (
                        <a
                          href={vendor.orderUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ms-2 inline-flex items-center gap-1 text-xs font-normal text-primary hover:underline"
                        >
                          Order online <ExternalLink className="size-3" aria-hidden="true" />
                        </a>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-foreground-muted">
                      <ContactSummary vendor={vendor} />
                    </td>
                    <td className="px-3 py-2 text-foreground-muted">{vendor.address ?? '—'}</td>
                    <td className="px-3 py-2">
                      <Switch
                        checked={vendor.isActive}
                        disabled={!canWrite}
                        onCheckedChange={(checked) =>
                          toggleActive.mutate({ id: vendor.id, isActive: checked })
                        }
                        aria-label={`${vendor.isActive ? 'Deactivate' : 'Activate'} ${vendor.name}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      {canWrite ? (
                        <Button variant="secondary" size="sm" onClick={() => setEditing(vendor)}>
                          Edit
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5}>
                    <EmptyState
                      icon={Truck}
                      title={search ? 'No matching vendors' : 'No vendors yet'}
                      description={
                        search
                          ? 'Try a different search.'
                          : 'Add a supplier to start building your vendor directory.'
                      }
                      variant={search ? 'no-results' : undefined}
                      onClearFilters={search ? () => setSearch('') : undefined}
                      action={
                        !search && canWrite
                          ? { label: 'Add a vendor', onClick: () => setCreating(true) }
                          : undefined
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <InfiniteScrollFooter
          total={total}
          loaded={items.length}
          hasMore={Boolean(hasNextPage)}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={() => void fetchNextPage()}
        />
      </div>

      <CreateVendorDialog open={creating} onOpenChange={setCreating} />
      <EditVendorDialog vendor={editing} onClose={() => setEditing(null)} />
    </Page>
  )
}

const PHONE_TYPE_LABELS: Record<(typeof VENDOR_PHONE_TYPES)[number], string> = {
  office: 'Office',
  mobile: 'Mobile',
  whatsapp: 'WhatsApp',
  fax: 'Fax',
  other: 'Other',
}
const EMAIL_TYPE_LABELS: Record<(typeof VENDOR_EMAIL_TYPES)[number], string> = {
  general: 'General',
  ordering: 'Ordering',
  support: 'Support',
  billing: 'Billing',
  sales: 'Sales',
  other: 'Other',
}

type DraftContact = {
  name: string
  role: string
  phones: { type: (typeof VENDOR_PHONE_TYPES)[number]; number: string }[]
  emails: { type: (typeof VENDOR_EMAIL_TYPES)[number]; address: string }[]
}

const emptyContact = (): DraftContact => ({ name: '', role: '', phones: [], emails: [] })

const toDraft = (c: VendorContact): DraftContact => ({
  name: c.name ?? '',
  role: c.role ?? '',
  phones: c.phones.map((p) => ({ type: p.type, number: formatUsPhone(p.number) })),
  emails: c.emails.map((e) => ({ type: e.type, address: e.address })),
})

/** Blank rows are dropped rather than rejected, so an unused "Add phone" click
 *  never blocks a save. */
function fromDraft(c: DraftContact) {
  return {
    name: c.name.trim() || null,
    role: c.role.trim() || null,
    phones: c.phones.filter((p) => p.number.trim()).map((p) => ({ ...p, number: p.number.trim() })),
    emails: c.emails
      .filter((e) => e.address.trim())
      .map((e) => ({ ...e, address: e.address.trim() })),
  }
}

function CreateVendorDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return <VendorFormDialog open={open} vendor={null} onClose={() => onOpenChange(false)} />
}

export function EditVendorDialog({
  vendor,
  onClose,
}: {
  vendor: Vendor | null
  onClose: () => void
}) {
  return <VendorFormDialog open={vendor !== null} vendor={vendor} onClose={onClose} />
}

function VendorFormDialog({
  open,
  vendor,
  onClose,
}: {
  open: boolean
  vendor: Vendor | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [orderUrl, setOrderUrl] = useState('')
  const [contacts, setContacts] = useState<DraftContact[]>([emptyContact()])
  const [address, setAddress] = useState('')
  const [notes, setNotes] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  // Reset whenever the dialog opens or a different vendor is passed in;
  // without this the previous vendor's values would linger.
  useEffect(() => {
    if (!open) return
    setFormError(null)
    setName(vendor?.name ?? '')
    setOrderUrl(vendor?.orderUrl ?? '')
    setContacts(
      vendor && vendor.contacts.length > 0 ? vendor.contacts.map(toDraft) : [emptyContact()],
    )
    setAddress(vendor?.address ?? '')
    setNotes(vendor?.notes ?? '')
  }, [open, vendor])

  const updateContact = (index: number, patch: Partial<DraftContact>) =>
    setContacts((all) => all.map((c, i) => (i === index ? { ...c, ...patch } : c)))

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name,
        orderUrl: orderUrl.trim() || null,
        contacts: contacts
          .map(fromDraft)
          .filter((c) => c.name || c.role || c.phones.length > 0 || c.emails.length > 0),
        address: address.trim() || null,
        notes: notes.trim() || null,
      }
      // The exact schema the API validates against, so the message shown is
      // the one the server would have returned.
      const parsed = createVendorSchema.safeParse(body)
      if (!parsed.success) {
        const issue = parsed.error.issues[0]
        throw new Error(
          issue
            ? `${issue.path.join(' › ')}: ${issue.message}`.replace(/^: /, '')
            : 'Invalid vendor',
        )
      }
      return vendor ? api.patch(`/vendors/${vendor.id}`, body) : api.post('/vendors', body)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.vendors.all() })
      toast.success(vendor ? 'Saved' : 'Vendor created')
      onClose()
    },
    onError: (error: Error) => {
      setFormError(error.message)
      toast.error(
        vendor ? 'Could not save' : 'Could not create the vendor',
        error instanceof ApiError ? error.message : undefined,
      )
    },
  })

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{vendor ? `Edit ${vendor.name}` : 'New vendor'}</DialogTitle>
        </DialogHeader>
        <DialogBody className="max-h-[70vh] space-y-4 overflow-y-auto">
          <Field label="Name" required>
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>

          <Field label="Online order link">
            {(props) => (
              <Input
                {...props}
                type="url"
                placeholder="https://"
                value={orderUrl}
                onChange={(e) => setOrderUrl(e.target.value)}
              />
            )}
          </Field>

          <div className="space-y-3">
            <p className="text-sm font-medium">Contacts</p>
            {contacts.map((contact, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: drafts have no id and are only appended/removed
              <div key={index} className="space-y-3 rounded-md border border-border p-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Contact name">
                    {(props) => (
                      <Input
                        {...props}
                        value={contact.name}
                        onChange={(e) => updateContact(index, { name: e.target.value })}
                      />
                    )}
                  </Field>
                  <Field label="Role">
                    {(props) => (
                      <Input
                        {...props}
                        placeholder="e.g. Sales rep"
                        value={contact.role}
                        onChange={(e) => updateContact(index, { role: e.target.value })}
                      />
                    )}
                  </Field>
                </div>

                <div className="space-y-2">
                  <p className="text-2xs font-medium uppercase tracking-wide text-foreground-muted">
                    Phones
                  </p>
                  {contact.phones.map((phone, pi) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: rows have no id
                    <div key={pi} className="flex items-center gap-2">
                      <Select
                        value={phone.type}
                        onValueChange={(v) =>
                          updateContact(index, {
                            phones: contact.phones.map((p, i) =>
                              i === pi ? { ...p, type: v as typeof phone.type } : p,
                            ),
                          })
                        }
                      >
                        <SelectTrigger className="w-32" aria-label="Phone type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {VENDOR_PHONE_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {PHONE_TYPE_LABELS[t]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        aria-label="Phone number"
                        value={phone.number}
                        onChange={(e) =>
                          updateContact(index, {
                            phones: contact.phones.map((p, i) =>
                              i === pi ? { ...p, number: e.target.value } : p,
                            ),
                          })
                        }
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label="Remove phone"
                        onClick={() =>
                          updateContact(index, {
                            phones: contact.phones.filter((_, i) => i !== pi),
                          })
                        }
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      updateContact(index, {
                        phones: [...contact.phones, { type: 'office', number: '' }],
                      })
                    }
                  >
                    <Plus /> Add phone
                  </Button>
                </div>

                <div className="space-y-2">
                  <p className="text-2xs font-medium uppercase tracking-wide text-foreground-muted">
                    Emails
                  </p>
                  {contact.emails.map((email, ei) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: rows have no id
                    <div key={ei} className="flex items-center gap-2">
                      <Select
                        value={email.type}
                        onValueChange={(v) =>
                          updateContact(index, {
                            emails: contact.emails.map((m, i) =>
                              i === ei ? { ...m, type: v as typeof email.type } : m,
                            ),
                          })
                        }
                      >
                        <SelectTrigger className="w-32" aria-label="Email type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {VENDOR_EMAIL_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {EMAIL_TYPE_LABELS[t]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="email"
                        aria-label="Email address"
                        value={email.address}
                        onChange={(e) =>
                          updateContact(index, {
                            emails: contact.emails.map((m, i) =>
                              i === ei ? { ...m, address: e.target.value } : m,
                            ),
                          })
                        }
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label="Remove email"
                        onClick={() =>
                          updateContact(index, {
                            emails: contact.emails.filter((_, i) => i !== ei),
                          })
                        }
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      updateContact(index, {
                        emails: [...contact.emails, { type: 'general', address: '' }],
                      })
                    }
                  >
                    <Plus /> Add email
                  </Button>
                </div>

                {contacts.length > 1 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setContacts((all) => all.filter((_, i) => i !== index))}
                  >
                    <Trash2 /> Remove contact
                  </Button>
                ) : null}
              </div>
            ))}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setContacts((all) => [...all, emptyContact()])}
            >
              <Plus /> Add contact
            </Button>
          </div>

          <Field label="Address">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            )}
          </Field>

          <Field label="Notes">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            )}
          </Field>

          {formError ? <p className="text-sm text-danger">{formError}</p> : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : vendor ? 'Save changes' : 'Create vendor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Compact summary of the first contact for the list; the rest sit behind the
 *  vendor's detail page. */
function ContactSummary({ vendor }: { vendor: Vendor }) {
  const first = vendor.contacts[0]
  if (!first) return <span>—</span>
  const phone = first.phones[0]
  const email = first.emails[0]
  const more = vendor.contacts.length - 1
  return (
    <div className="space-y-0.5">
      {first.name ? (
        <div className="text-foreground">
          {first.name}
          {first.role ? <span className="text-foreground-muted"> · {first.role}</span> : null}
        </div>
      ) : null}
      {phone ? (
        <div>
          {formatUsPhone(phone.number)}{' '}
          <span className="text-2xs">({PHONE_TYPE_LABELS[phone.type]})</span>
        </div>
      ) : null}
      {email ? (
        <div>
          {email.address} <span className="text-2xs">({EMAIL_TYPE_LABELS[email.type]})</span>
        </div>
      ) : null}
      {more > 0 ? (
        <div className="text-2xs">
          +{more} more contact{more > 1 ? 's' : ''}
        </div>
      ) : null}
    </div>
  )
}
